import { FC, ReactNode, useMemo } from 'react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Typography } from '@mui/material';
import { ProviderAccordion, ProviderPreview } from 'components/shared';
import { PROVIDER_TYPE } from 'utils/enums';
import {
	findOtherSelectedServiceInSingleReceipt,
	findProviderSelectedServiceInSingleReceipt,
	getAmountTitle,
	getPayerFullName,
	getProvideIconByType,
	getProviderTitle,
} from 'utils/helpers';
import {
	IPaymentPayerFormData,
	IProviderServiceResponse,
	IReceiptOtherServiceResponse,
	IReceiptResponse,
	IServiceRegData,
	ISingleReceiptPayProviderServiceRequest,
} from 'utils/types';
import styles from './index.module.scss';

interface ReceiptPaymentInstructionProps {
	receipt: IReceiptResponse;
	selectedServiceList: ISingleReceiptPayProviderServiceRequest[];
	payerData: IPaymentPayerFormData;
}

interface InstructionItem {
	id: string;
	title: string;
	icon: string;
	iban: string;
	amount: number;
	reservedBillId?: string;
	regData: Partial<IServiceRegData>;
	providerType: PROVIDER_TYPE;
	code: string;
	previewData: IProviderServiceResponse | IReceiptOtherServiceResponse;
}

const REG_DATA_FIELDS: { key: keyof IServiceRegData; label: string }[] = [
	{ key: 'name', label: 'Отримувач' },
	{ key: 'account', label: 'IBAN отримувача' },
	{ key: 'zkpo', label: 'ЄДРПОУ' },
	{ key: 'bankName', label: 'Банк отримувача' },
	{ key: 'bankMfo', label: 'МФО банку' },
	{ key: 'description', label: 'Призначення платежу' },
];

const getUniqueValues = (values: (string | undefined)[]): string =>
	Array.from(new Set(values.map((value) => value?.trim()).filter(Boolean) as string[])).join('; ');

const mergeRegData = (services: { regDataObject?: IServiceRegData }[]): Partial<IServiceRegData> => {
	const serviceTitles = getUniqueValues(services.map((item) => item.regDataObject?.serviceTitle));

	return {
		account: getUniqueValues(services.map((item) => item.regDataObject?.account)),
		bankMfo: getUniqueValues(services.map((item) => item.regDataObject?.bankMfo)),
		bankName: getUniqueValues(services.map((item) => item.regDataObject?.bankName)),
		description: serviceTitles || getUniqueValues(services.map((item) => item.regDataObject?.description)),
		name: getUniqueValues(services.map((item) => item.regDataObject?.name)),
		zkpo: getUniqueValues(services.map((item) => item.regDataObject?.zkpo)),
	};
};

const InstructionDetails: FC<{ item: InstructionItem }> = ({ item }) => (
	<div className={styles.details}>
		{item.reservedBillId && (
			<div className={styles.details__row}>
				<Typography variant="body2" color="var(--color-neutral-500)">
					Номер платіжної інструкції
				</Typography>
				<Typography variant="body2">{item.reservedBillId}</Typography>
			</div>
		)}
		{REG_DATA_FIELDS.map(({ key, label }) => {
			const value = item.regData[key];
			return value ? (
				<div className={styles.details__row} key={key}>
					<Typography variant="body2" color="var(--color-neutral-500)">
						{label}
					</Typography>
					<Typography variant="body2">{value}</Typography>
				</div>
			) : null;
		})}
		<div className={styles.details__row}>
			<Typography variant="body2" color="var(--color-neutral-500)">
				Сума до сплати
			</Typography>
			<Typography variant="body2">{getAmountTitle(item.amount)}</Typography>
		</div>
	</div>
);

const InstructionSection: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
	<section className={styles.section}>
		<Typography variant="h6">{title}</Typography>
		<div className={styles.section__list}>{children}</div>
	</section>
);

const InstructionServiceItem: FC<{ item: InstructionItem }> = ({ item }) => (
	<ProviderAccordion
		className={styles.accordion}
		expandIcon={<ExpandMoreRoundedIcon />}
		title={
			<ProviderPreview
				title={item.title}
				description={
					<>
						{`IBAN: ${item.iban || '—'}, `}
						<span className={styles.previewAmount}>{getAmountTitle(item.amount)}</span>
					</>
				}
				icon={item.icon}
				code={item.code}
				providerType={item.providerType}
				data={item.previewData}
				isHideQuestion
				isCompact
			/>
		}
	>
		<InstructionDetails item={item} />
	</ProviderAccordion>
);

export const ReceiptPaymentInstruction: FC<ReceiptPaymentInstructionProps> = ({
	receipt,
	selectedServiceList,
	payerData,
}) => {
	const ultimateDebtor = payerData.isPayerSameAsUltimateDebtor ? payerData : payerData.ultimateDebtor;
	const communalItems = useMemo<InstructionItem[]>(
		() =>
			Object.entries(receipt.serviceProviderData).reduce((result, [providerKey, services]) => {
				const selectedServices = services.filter((service) =>
					selectedServiceList.some(
						(selectedItem) =>
							Number(selectedItem.incomeBalance) > 0 &&
							findProviderSelectedServiceInSingleReceipt(service, selectedItem)
					)
				);
				if (!selectedServices.length) return result;

				const amount = selectedServices.reduce((total, service) => {
					const selectedItem = selectedServiceList.find((item) =>
						findProviderSelectedServiceInSingleReceipt(service, item)
					);
					return total + Number(selectedItem?.incomeBalance ?? 0);
				}, 0);
				const firstService = selectedServices[0];

				if (providerKey === PROVIDER_TYPE.PORTMONE) {
					const portmoneItems = selectedServices.map((service, index) => {
						const selectedItem = selectedServiceList.find((item) =>
							findProviderSelectedServiceInSingleReceipt(service, item)
						);
						const regData = mergeRegData([service]);

						return {
							id: `portmone-${service.id}-${selectedItem?.billId ?? index}`,
							title: getProviderTitle(service),
							icon: getProvideIconByType(service.providerType, service),
							iban: regData.account ?? '',
							amount: Number(selectedItem?.incomeBalance ?? 0),
							reservedBillId: receipt.communalPaymentReservedBillId,
							regData,
							providerType: service.providerType,
							code: service.code,
							previewData: service,
						};
					});

					return [...result, ...portmoneItems];
				}

				const regData = mergeRegData(selectedServices);

				return [
					...result,
					{
						id: `communal-${providerKey}`,
						title: getProviderTitle(firstService),
						icon: getProvideIconByType(firstService.providerType, firstService),
						iban: regData.account ?? '',
						amount,
						reservedBillId: receipt.communalPaymentReservedBillId,
						regData,
						providerType: firstService.providerType,
						code: firstService.code,
						previewData: firstService,
					},
				];
			}, [] as InstructionItem[]),
		[receipt, selectedServiceList]
	);

	const otherItems = useMemo<InstructionItem[]>(
		() =>
			receipt.otherPaymentServices.reduce((result, service) => {
				const selectedItem = selectedServiceList.find(
					(item) => Number(item.incomeBalance) > 0 && findOtherSelectedServiceInSingleReceipt(service, item)
				);
				if (!selectedItem) return result;

				const regData = mergeRegData([service]);
				return [
					...result,
					{
						id: `other-${service.id}-${selectedItem.billId ?? ''}`,
						title: service.title ?? service.name,
						icon: getProvideIconByType(service.provider, service),
						iban: regData.account ?? '',
						amount: Number(selectedItem.incomeBalance),
						reservedBillId: service.reservedBillId,
						regData,
						providerType: service.provider,
						code: service.code,
						previewData: service,
					},
				];
			}, [] as InstructionItem[]),
		[receipt.otherPaymentServices, selectedServiceList]
	);

	return (
		<div className={styles.wrapper}>
			{!!communalItems.length && (
				<InstructionSection title="Єдина квитанція">
					{communalItems.map((item) => (
						<InstructionServiceItem item={item} key={item.id} />
					))}
				</InstructionSection>
			)}

			{!!otherItems.length && (
				<InstructionSection title="Інші платежі">
					{otherItems.map((item) => (
						<InstructionServiceItem item={item} key={item.id} />
					))}
				</InstructionSection>
			)}

			<section className={styles.payer}>
				<Typography variant="h6">Введені дані</Typography>
				<div className={styles.payer__persons}>
					<div className={styles.payer__person}>
						<Typography variant="subtitle2">Платник</Typography>
						<div className={styles.payer__data}>
							<div>
								<Typography variant="body2" color="var(--color-neutral-500)">
									ПІБ
								</Typography>
								<Typography variant="body1">{getPayerFullName(payerData)}</Typography>
							</div>
							<div>
								<Typography variant="body2" color="var(--color-neutral-500)">
									ІПН (РНОКПП)
								</Typography>
								<Typography variant="body1">{payerData.taxId}</Typography>
							</div>
						</div>
					</div>
					<div className={styles.payer__person}>
						<Typography variant="subtitle2">Фактичний платник</Typography>
						<div className={styles.payer__data}>
							<div>
								<Typography variant="body2" color="var(--color-neutral-500)">
									ПІБ
								</Typography>
								<Typography variant="body1">{getPayerFullName(ultimateDebtor)}</Typography>
							</div>
							<div>
								<Typography variant="body2" color="var(--color-neutral-500)">
									ІПН (РНОКПП)
								</Typography>
								<Typography variant="body1">{ultimateDebtor.taxId}</Typography>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>
	);
};
